import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
import { Box, Typography } from '@mui/material';
import { areConnectorsCompatible } from '../services/compatibilityMatrix';

/**
 * Props:
 *   Either:
 *     result: { status: 'compatible' | 'warning' | 'incompatible', message, notes }
 *   Or:
 *     connector: connector object
 *     selectedPoint: point ID
 *     allConnectors: all connectors object
 */
export default function ConnectorCompatibilityIndicator({ result, connector, selectedPoint, allConnectors }) {
  // If result is provided directly, use it
  if (result) {
    if (!result) return null;
    let icon, color, label;
    switch (result.status) {
      case 'compatible':
        icon = <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20, mr: 1 }} titleAccess="Compatible" />;
        color = 'success.main';
        label = 'Compatible';
        break;
      case 'warning':
        icon = <WarningIcon sx={{ color: '#ffb300', fontSize: 20, mr: 1 }} titleAccess="Compatible with warning" />;
        color = 'warning.main';
        label = 'Compatible with warning';
        break;
      case 'incompatible':
      default:
        icon = <CancelIcon sx={{ color: '#f44336', fontSize: 20, mr: 1 }} titleAccess="Incompatible" />;
        color = 'error.main';
        label = 'Incompatible';
        break;
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', color }}>
        {icon}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{label}</Typography>
          {result.message && (
            <Typography variant="caption" sx={{ display: 'block' }}>{result.message}</Typography>
          )}
          {result.notes && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{result.notes}</Typography>
          )}
        </Box>
      </Box>
    );
  }

  // If individual props are provided, calculate compatibility
  if (connector && selectedPoint && allConnectors) {
    // Find the paired connector for this point
    const pairs = { 'I': 'J', 'J': 'I', 'K': 'L', 'L': 'K', 'M': 'N', 'N': 'M' };
    const pairedPoint = pairs[selectedPoint];
    const pairedConnector = allConnectors[pairedPoint];

    if (!pairedConnector?.id) {
      return null; // No paired connector to check against
    }

    try {
      const compatibilityResult = areConnectorsCompatible(pairedConnector.id, connector.id);
      
      if (!compatibilityResult) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
            <CancelIcon sx={{ color: '#f44336', fontSize: 20, mr: 1 }} titleAccess="Incompatible" />
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Incompatible</Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>Not compatible</Typography>
            </Box>
          </Box>
        );
      }
      
      if (compatibilityResult.compatible === false) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
            <CancelIcon sx={{ color: '#f44336', fontSize: 20, mr: 1 }} titleAccess="Incompatible" />
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Incompatible</Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>{compatibilityResult.reason || 'Incompatible'}</Typography>
              {compatibilityResult.details && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{compatibilityResult.details}</Typography>
              )}
            </Box>
          </Box>
        );
      }
      
      if (compatibilityResult.warning) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}>
            <WarningIcon sx={{ color: '#ffb300', fontSize: 20, mr: 1 }} titleAccess="Compatible with warning" />
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Compatible with warning</Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>{compatibilityResult.warning}</Typography>
              {compatibilityResult.notes && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{compatibilityResult.notes}</Typography>
              )}
            </Box>
          </Box>
        );
      }
      
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
          <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20, mr: 1 }} titleAccess="Compatible" />
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Compatible</Typography>
          </Box>
        </Box>
      );
    } catch (error) {
      console.warn('Error checking compatibility:', error);
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
          <CancelIcon sx={{ color: '#f44336', fontSize: 20, mr: 1 }} titleAccess="Error" />
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Error</Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>Error checking compatibility</Typography>
          </Box>
        </Box>
      );
    }
  }

  return null;
} 